import React, { useState, useEffect, useRef } from 'react';
import { getBlurPlaceholder, getImageAspectRatio } from '../utils/imageOptimizer';
import './OptimizedImage.css';

/**
 * OptimizedImage Component
 * Features:
 * - Lazy loading with Intersection Observer
 * - Blur-up progressive image loading effect
 * - Responsive images
 * - Fallback error handling
 * - Native lazy loading attribute
 */
export const OptimizedImage = ({
  src,
  alt = 'Image',
  className = '',
  width = 400,
  height = 300,
  onClick = null,
  title = '',
  placeholder = true,
  quality = 'medium',
  objectFit = 'cover',
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [blurState, setBlurState] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!src) return;

    // Set up Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(src);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [src]);

  const handleImageLoad = () => {
    setBlurState(false);
    setIsLoading(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
    // Fallback: Set a placeholder
    setImageSrc(
      `https://via.placeholder.com/${width}x${height}?text=${encodeURIComponent(
        alt
      )}`
    );
  };

  const aspectRatio = getImageAspectRatio(width, height);
  const placeholderSrc = placeholder
    ? getBlurPlaceholder(src)
    : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E';

  return (
    <div
      ref={containerRef}
      className={`optimized-image-container ${className}`}
      style={{
        paddingBottom: `${aspectRatio}%`,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f0f0f0',
      }}
      onClick={onClick}
      role={onClick ? 'button' : 'img'}
      title={title}
    >
      {/* Placeholder Image - Shows while loading */}
      {placeholder && isLoading && !hasError && (
        <img
          src={placeholderSrc}
          alt=""
          className={`optimized-image-placeholder blur ${blurState ? 'blur-active' : ''}`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: objectFit,
          }}
          aria-hidden="true"
        />
      )}

      {/* Main Image */}
      {imageSrc && !hasError && (
        <img
          ref={imageRef}
          src={imageSrc}
          alt={alt}
          className={`optimized-image ${blurState ? 'blur-active' : ''} ${
            isLoading ? 'loading' : 'loaded'
          }`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: objectFit,
            transition: blurState ? 'none' : 'filter 0.3s ease-out',
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="lazy"
          width={width}
          height={height}
        />
      )}

      {/* Error Fallback */}
      {hasError && (
        <div
          className="optimized-image-error"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#e0e0e0',
            color: '#666',
            fontSize: '12px',
          }}
        >
          <span>Image failed to load</span>
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && !hasError && (
        <div
          className="optimized-image-loader"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '30px',
            height: '30px',
          }}
        >
          <div className="spinner" />
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
